import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injectable, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { injectStripe, StripeElementsDirective, StripePaymentElementComponent } from 'ngx-stripe';
import { backend, stripe_key } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { debounceTime, filter, from, map, of, switchMap, take } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { BillingDetails, PaymentIntent, SetupIntent, StripeElementsOptions } from '@stripe/stripe-js';
import { NgTemplateOutlet } from '@angular/common';
import { cardTypes } from '../types';
import { DateTime } from 'luxon';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { PaintDirective } from '../paint.directive';
import { ToastrService } from 'ngx-toastr';
import { ConfirmPopup } from '../confirm.popup';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmPopupComponent } from '../confirm-popup/confirm-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatError, MatHint } from '@angular/material/form-field';
import { HttpClient } from '@angular/common/http';

const MONTHLYFORMAT = {
  parse: {
    dateInput: 'LL/yyyy',
  },
  display: {
    dateInput: 'LL/yyyy',
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'LLLL yyyy',
  },
};

type CardSelectorState = 'preview' | 'list' | 'add';

interface IStripeCreatedCard {
  session: string;
  client_secret: string;
  id: string;
  type: 'payment_intent' | 'setup_intent';
}

export interface ICreditCard {
  brand: string;
  exp_month: number;
  exp_year: number;
  last4: string;
}

export interface IStripeCard {
  _id: string;
  token: string;
  intent_type: 'setup' | 'payment';
  intent_id: string;
  card: ICreditCard;
  preferred: string;
}

interface IStripeCompletedSetup {
  result: IStripeCard;
  membership?: string;
  transaction?: string;
  retry?: string;
}

export interface IStripeList {
  result: IStripeCard[];
  transaction?: string;
}


export type Target = 'package' | 'coupon' | undefined;
export type IntentPayload = { target: Target; package_id?: string; use_id?: string; intent?: string; };
export type IntentResponse = { id: string; client_secret: string; customer?: string; 
type: 'setup_intent' | 'payment_intent' };


@Injectable({
  providedIn: 'root'
})
export class CardService {
  http = inject(HttpClient);
  stripe = injectStripe(stripe_key);

  upsertIntent(payload: IntentPayload){
    return this.http.post<IntentResponse>(backend + 'intent/upsert', payload);
  }
  
  createSetup(package_id?: string, use_id?: string){
    return this.http.post<IStripeCreatedCard>(backend + 'checkout/card/create', { package_id, use_id});
  }

  completeSetup(setup: string, transaction?: string){
    return this.http.post<IStripeCompletedSetup>(backend + 'checkout/card/process', { setup, transaction });
  }

  updatePayment(intent_id: string, package_id?: string, use_id?: string){
    return this.http.post<IStripeCreatedCard>(backend + 'checkout/payment/update', { intent_id, package_id, use_id });
  }

  preferCard(intent_id: string){
    return this.http.post<IStripeList>(backend + 'checkout/card/prefer', { intent_id });
  }

  editCard(intent_id: string, exp_month: number, exp_year: number){
    return this.http.post<IStripeList>(backend + 'checkout/card/update', { intent_id, exp_month, exp_year });
  }

  deleteCard(intent_id: string){
    return this.http.post<IStripeList>(backend + 'checkout/card/delete', { intent_id });
  }

  listCards(scenario?: string){
    return this.http.post<IStripeList>(backend + 'checkout/card/list', { scenario });
  }

}

type ViewState = 'selected' | 'list' | 'add';

export type CardAddedEvent = { 
  _id: string; 
  membership?: string; 
  transaction?: string; 
  should_sync?: boolean
}

@Component({
  selector: 'card-input',
  standalone: true,
  imports: [StripePaymentElementComponent, StripeElementsDirective, NgTemplateOutlet, MatIconModule, MatButtonModule, PaintDirective, MatDatepickerModule, ReactiveFormsModule, MatHint, MatError], 
  providers: [
    provideLuxonDateAdapter(MONTHLYFORMAT),
  ],
  templateUrl: './card-input.component.html',
  styleUrl: './card-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardInputComponent {
  auth = inject(AuthService);
  destroy = inject(DestroyRef);
  api = inject(CardService);
  toast = inject(ToastrService);
  dialogs = inject(MatDialog);
  confirm = inject(ConfirmPopup);

  payment_element = viewChild<StripePaymentElementComponent>(StripePaymentElementComponent);
  setup_element = viewChild<StripePaymentElementComponent>(StripePaymentElementComponent);
  update_form = viewChild<TemplateRef<any>>('updateForm');

  target = input<Target>(undefined);
  package = input<string|undefined>();
  use = input<string|undefined>();
  manual = input<boolean>(false);

  intent_variables = signal<IntentResponse|null>(null);
  loading = signal<boolean>(true);
  selected_view = signal<ViewState>('add');
  payment_methods = signal<IStripeCard[]>([]);
  selected_method_id = signal<string|null>(null);

  paymentComplete = output<{ intent: PaymentIntent, payload: any }>();
  setupComplete = output<SetupIntent>();

  cardAdded = output<CardAddedEvent>();
  cardUpdated = output<string>();//UNUSED
  cardDeleted = output<string>();
  cardSelected = output<string>();
  loaded = output<void>();

  selected_method = computed(() => {
    return this.payment_methods()?.find(method => method.intent_id == this.selected_method_id());
  });

  intent_settings = computed(() => {
    const target = this.target();
    const pkg = this.package();
    const use = this.use();

    // console.log('intent settings updated', target, pkg, use);

    const result: IntentPayload = { target: target };

    switch(target){
      //@ts-ignore //!FALLTHROUGH INTO PACKAGE ON PURPOSE
      case 'package': result.use_id = use;
      case 'coupon':
        result.package_id = pkg;
      break;
    }

    return JSON.stringify(result);
  });

  elements_options = computed<StripeElementsOptions>(() => {
    const payload = this.intent_variables();
    return {
      locale: 'en',
      clientSecret: payload?.client_secret,
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap'
        }
      ],
      appearance: {
        theme: 'stripe',
        variables: {
          fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif'
        }
      }
    }
  });

  minDate = DateTime.now().set({ day: 10 });
  today = DateTime.now().toFormat('yyyy-MM').split('-').map(Number);
  cardTypes = cardTypes;
  expiry = new FormControl<DateTime|null>(null, [Validators.required]);

  constructor(){
    from(this.auth.fireAuth.authStateReady()).pipe(
      takeUntilDestroyed(this.destroy),
      switchMap(() => this.auth.fireState$),
      switchMap(state => {
        if(state){
          return this.api.listCards();
        } else {
          // console.log('no state, no cards');
          return of<IStripeList>({ result: [] });//fake result if we're not logged in
        }
      })
    ).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: response => {
        // console.log('set cards', response?.result);
        this.payment_methods.set(response.result);

        if(response.result.length){
          this.selectPreferred();
          this.selected_view.set(this.target() ? 'selected' : 'list');
        }
      },
      error: error => {
        console.warn(error);
        this.payment_methods.set([]);
      }
    });

    toObservable(this.intent_settings).pipe(
      takeUntilDestroyed(this.destroy),
      switchMap((settings) => from(this.auth.fireAuth.authStateReady()).pipe(map(() => settings))),
      debounceTime(400),
      map<string, IntentPayload>(payload_string => {
        // console.log('intent settings', payload_string);
        const payload = JSON.parse(payload_string);
        payload.intent = this.intent_variables()?.id;
        return payload;
      }),
      switchMap(payload => this.api.upsertIntent(payload))
    ).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: response => {
        // console.log('intent data', response);
        if(response?.id){
          this.intent_variables.set(response);
        }

        // if(!this.target()){
          this.loading.set(false);
        // }
      },
      error: error => {
        if(error.code){
          switch(error.code){
            case 2:
              this.auth.sync();
            break;
          }
        }

        this.selected_method_id.set(null);

        this.toast.error(error.message);
      }
    })
  }

  private selectPreferred(): void {
    const preferred_method = [...this.payment_methods()].sort((a,b) => a.preferred < b.preferred ? 1 : -1).shift();

    if(preferred_method){
      this.selected_method_id.set(preferred_method.intent_id);
    }

  }

  submitIntent(billing_details?: BillingDetails, payload?: any): void {
    const { type } = this.intent_variables() || {};

    // console.log(this.intent_variables());

    if(!type){
      return;
    }

    this.loading.set(true);

    of(type).pipe(
      map(type => this.payment_element()),
      filter(component => !!component),
      switchMap(component => {
        const confirmFn = type == 'payment_intent' ? 'confirmPayment': 'confirmSetup';
        return this.api.stripe[confirmFn]({
          elements: component.elements,
          redirect: 'if_required',
          confirmParams: {
            payment_method_data: {
              billing_details
            }
          }
        });
      })
    ).pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: (confirmResult) => {
        if(confirmResult.error){
          this.loading.set(false);

        } else {

          if('paymentIntent' in confirmResult){
            this.paymentComplete.emit({ intent: confirmResult.paymentIntent, payload });
          } else {
            this.api.completeSetup(confirmResult.setupIntent.id).pipe(takeUntilDestroyed(this.destroy)).subscribe({
              next: response => {
                const payment_method = response.result || {
                  _id: 'new',
                  preferred: new Date(),
                  intent_type: 'setup',
                  intent_id: confirmResult.setupIntent.id
                };

                this.payment_methods.update(methods => [...methods, payment_method]);
                this.selectPreferred();
                this.setupComplete.emit(confirmResult.setupIntent);
                this.cardAdded.emit({ _id: response.result._id, membership: response.membership, transaction: response.transaction, should_sync: response.retry == 'COMPLETED' });
                this.selected_view.set('list');

                this.loading.set(false);
              },
              error: error => {
                this.loading.set(false);
              }
            })
          }
        }
      }
    });

  }

  selectCard(payment_method: string){
    if(this.target()){
      this.selected_method_id.set(payment_method);
      this.cardSelected.emit(payment_method);
      this.selected_view.set('selected');
    } else {
      this.loading.set(true);

        this.api.preferCard(payment_method).pipe(take(1)).subscribe({
          next: response => {
            this.payment_methods.set(response.result || []);

            this.selectPreferred();
            this.cardSelected.emit(payment_method);
    
            this.selected_view.set('list');
            this.loading.set(false);
          },
          error: error => {
            this.toast.error(`There was a problem with selecting your preferred card. (${error.message})`);
            this.loading.set(false);
          }
        });
    }
  }

  onFocus(event: FocusEvent, picker: MatDatepicker<Date>){
    (event.target as HTMLElement).blur();

    if(picker){
      picker.open();
    }

  }

  updateCard(payment_method: string, card: ICreditCard){
    if(this.dialogs.openDialogs.find(ref => ref.componentInstance instanceof ConfirmPopupComponent && ref.componentInstance.input.title == 'Update card details')){
      return;
    }
    
    let expiryDate = DateTime.now().set({ day: 15 }).startOf('day').set({
      month: card.exp_month,
      year: card.exp_year,
    });

    this.expiry.reset(expiryDate);
    this.expiry.markAsPristine();
    this.expiry.markAsUntouched();

    const updateForm = this.update_form();

    if(updateForm){
      this.confirm.open('Update card details', updateForm, 'Update Payment Method Form', [
        {
          label: 'Submit',
          paint: 'primary',
          callback: (ref) => {
            if(!this.expiry.valid){
              this.expiry.markAsTouched();
              return;
            }
            if(this.expiry.value && this.expiry.valid && expiryDate != this.expiry.value){
              const date = this.expiry.value;
              const month = date.month;//? 0-indexed
              const year = date.year;
      
              this.loading.set(true);
              this.api.editCard(payment_method, month, year).pipe(take(1)).subscribe({
                next: response => {
                  if(response.result){
                    
                    this.payment_methods.set(response.result);

                    this.selectPreferred();
                    this.cardSelected.emit(payment_method);
            
                    this.selected_view.set('list');
                    this.loading.set(false);
    
                    ref.close();
                    this.expiry.reset(null);
                    this.expiry.markAsPristine();
                    this.expiry.markAsUntouched();
                  }
                },
                error: error => {
                  error = error.error || error;
                  this.toast.error(`There was a problem with updating your card.<br/>${error.message}`, undefined, { enableHtml: true });
                  this.loading.set(false);
                  this.expiry.markAsTouched();
                }
              });
            }
          }
        }
      ], 'Close');
    }
  }

  deleteCard(payment_method: string){
    this.confirm.open('Delete Payment Method?', 'Are you sure you would like to remove this payment method?', 'Delete Payment Method Confirmation', [
      {
        label: 'No',
        paint: 'primary',
        close: true
      },
      {
        label: 'Yes',
        paint: 'warn',
        close: true,
        callback: (ref) => {
          this.loading.set(true);

          this.api.deleteCard(payment_method).pipe(take(1)).subscribe({
            next: response => {
              if(response.result){
                this.payment_methods.set(response.result);
                this.cardDeleted.emit(payment_method);
              }
              this.loading.set(false);
              ref.close();
            },
            error: error => {
              error = error.error || error;
              this.toast.error( `There was a problem with deleting your card.<br/>${error.message}`, undefined, { enableHtml: true });
              this.loading.set(false);
            }
          })
        }
      }
    ]);
  }

}
